import { ArrowDownward, ArrowForward, DeleteSweepOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode, RefObject, memo, useState } from 'react';

import { MarketType } from '../../../../../../@types/api';
import { useBreakpoints } from '../../../../../../hooks/useBreakpoints';
import ConfirmationModal from '../../../../../modals/ConfirmationModal';
import { getMarketTypeName } from '../../../../../../helpers';
import RemoveExclusionButton from '../RemoveExclusionButton';
import MarketFilters from './MarketFilters';
import { useMarketPicker } from './MarketPickerContext';
import { formatMarketTypeContext } from '../marketExclusions.utils';

const styles = {
  panel: {
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    height: { xs: 'auto', md: 580 },
    minHeight: 480,
    overflow: 'hidden',
  },
  header: {
    alignItems: 'center',
    borderBottom: 1,
    borderColor: 'divider',
    display: 'flex',
    height: 40,
    minHeight: 40,
    px: 2,
  },
  headerAction: { ml: 'auto' },
  list: { flex: 1, overflowY: 'auto' },
  checkboxColumn: { minWidth: 40 },
  selectAll: { px: 2, borderBottom: 1, borderColor: 'divider' },
  listItem: { px: 2, py: 0.5 },
  empty: { flex: 1, p: 2 },
  loader: { justifyContent: 'center', minHeight: 48 },
  confirmationPaper: { borderRadius: 4, minWidth: 416, p: 3 },
} as const;

const PickerPanel = ({
  title,
  action,
  children,
  footer,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) => (
  <Box sx={styles.panel}>
    <Box sx={styles.header}>
      <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {action && <Box sx={styles.headerAction}>{action}</Box>}
    </Box>
    {children}
    {footer}
  </Box>
);

const MarketTypeListItemText = ({ marketType }: { marketType: MarketType }) => (
  <ListItemText
    primary={getMarketTypeName(marketType)}
    secondary={formatMarketTypeContext(marketType)}
    slotProps={{ primary: { variant: 'body2' } }}
  />
);

type AvailableListProps = {
  marketTypes: MarketType[];
  selectedIds: Set<string>;
  checkedIds: Set<string>;
  onToggle: (id: string) => void;
  listRef: RefObject<HTMLUListElement | null>;
  lastElementRef: (node: HTMLElement | null) => void;
  isFetchingNextPage: boolean;
  isLoading: boolean;
};

const AvailableMarketTypesList = memo(function AvailableMarketTypesList({
  marketTypes,
  selectedIds,
  checkedIds,
  onToggle,
  listRef,
  lastElementRef,
  isFetchingNextPage,
  isLoading,
}: AvailableListProps) {
  return marketTypes.length ? (
    <List ref={listRef} dense disablePadding sx={styles.list}>
      {marketTypes.map((marketType) => {
        const isSelected = selectedIds.has(marketType.id);
        return (
          <ListItem key={marketType.id} disablePadding>
            <ListItemButton disabled={isSelected} onClick={() => onToggle(marketType.id)} dense>
              <ListItemIcon sx={styles.checkboxColumn}>
                <Checkbox
                  edge='start'
                  size='small'
                  checked={isSelected || checkedIds.has(marketType.id)}
                  tabIndex={-1}
                  disableRipple
                  disabled={isSelected}
                />
              </ListItemIcon>
              <MarketTypeListItemText marketType={marketType} />
            </ListItemButton>
          </ListItem>
        );
      })}
      <ListItem ref={lastElementRef} sx={styles.loader}>
        {isFetchingNextPage && <CircularProgress size={20} />}
      </ListItem>
    </List>
  ) : (
    <Typography color='text.secondary' variant='body2' sx={styles.empty}>
      {isLoading ? 'Loading market types...' : 'No market types found.'}
    </Typography>
  );
});

export const AvailableMarketTypesPanel = () => {
  const { state, actions, meta } = useMarketPicker();
  const { isDesktop } = useBreakpoints();
  return (
    <PickerPanel
      title='Markets'
      footer={
        <>
          <MarketFilters />
          <Box sx={{ px: 2, pb: 2 }}>
            <Button
              variant='contained'
              fullWidth
              onClick={actions.addSelected}
              disabled={!state.checkedMarketTypeIds.size}
              aria-label='Add selected market types'
              endIcon={isDesktop ? <ArrowForward /> : <ArrowDownward />}
            >
              Add selected ({state.checkedMarketTypeIds.size})
            </Button>
          </Box>
        </>
      }
    >
      <Box sx={styles.selectAll}>
        <Tooltip title='Select all loaded markets' describeChild>
          <FormControlLabel
            label='Select all'
            disabled={meta.isLoading || !state.hasEligibleMarketTypes}
            control={
              <Checkbox
                size='small'
                checked={state.allLoadedChecked}
                indeterminate={state.someLoadedChecked}
                onChange={(_, checked) => actions.setAllLoadedChecked(checked)}
                slotProps={{ input: { 'aria-checked': state.someLoadedChecked ? 'mixed' : state.allLoadedChecked } }}
              />
            }
          />
        </Tooltip>
      </Box>
      <AvailableMarketTypesList
        marketTypes={state.availableMarketTypes}
        selectedIds={state.selectedMarketTypeIds}
        checkedIds={state.checkedMarketTypeIds}
        onToggle={actions.toggleMarketType}
        listRef={meta.listRef}
        lastElementRef={meta.lastElementRef}
        isFetchingNextPage={meta.isFetchingNextPage}
        isLoading={meta.isLoading}
      />
    </PickerPanel>
  );
};

const SelectedMarketTypesList = memo(function SelectedMarketTypesList({
  marketTypes,
  onRemove,
}: {
  marketTypes: MarketType[];
  onRemove: (id: string) => void;
}) {
  return marketTypes.length ? (
    <List dense disablePadding sx={styles.list}>
      {marketTypes.map((marketType) => (
        <ListItem key={marketType.id} sx={styles.listItem}>
          <MarketTypeListItemText marketType={marketType} />
          <RemoveExclusionButton
            label={`Remove ${getMarketTypeName(marketType)}`}
            onClick={() => onRemove(marketType.id)}
          />
        </ListItem>
      ))}
    </List>
  ) : (
    <Typography color='text.secondary' variant='body2' sx={styles.empty}>
      No market types selected.
    </Typography>
  );
});

export const SelectedMarketTypesPanel = () => {
  const { state, actions } = useMarketPicker();
  const [isClearConfirmationOpen, setIsClearConfirmationOpen] = useState(false);
  const closeClearConfirmation = () => setIsClearConfirmationOpen(false);

  return (
    <>
      <PickerPanel
        title='Selected exclusions'
        action={
          <Tooltip title='Clear all' placement='right'>
            <span>
              <IconButton
                color='error'
                size='small'
                aria-label='Clear all'
                onClick={() => setIsClearConfirmationOpen(true)}
                disabled={!state.selectedMarketTypes.length}
              >
                <DeleteSweepOutlined fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        }
      >
        <SelectedMarketTypesList marketTypes={state.selectedMarketTypes} onRemove={actions.removeMarketType} />
      </PickerPanel>

      <Dialog
        open={isClearConfirmationOpen}
        onClose={closeClearConfirmation}
        slotProps={{ paper: { sx: styles.confirmationPaper } }}
      >
        <ConfirmationModal
          title='Clear all exclusions?'
          actionButtonClose='Cancel'
          actionButtonLabel='Clear all'
          handleConfirm={actions.clearAll}
          closeModal={closeClearConfirmation}
        />
      </Dialog>
    </>
  );
};
